import { Grid, Stack } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import { Select } from "@/components/elements/Select";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { useFormContext, useWatch } from "react-hook-form";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { ERecruitmentStatus } from "@/types/enum";
import { useEffect, useRef } from "react";

export const TalentIndustryFields = () => {
  const { control, setValue } = useFormContext<TTalentForm>();
  const status = useWatch({ name: "recruitmentStatus" });
  const isDraft = status === ERecruitmentStatus.DRAFT;

  const companySpecificIndustryId = useWatch({ control, name: "companySpecificIndustryId" }) as number | null | undefined;
  const specificBusinessClassificationIds = useWatch({ control, name: "specificBusinessClassificationIds" }) as
    | (number | null | undefined)[]
    | null
    | undefined;
  const prevCompanySpecificIndustryIdRef = useRef<number | null | undefined>(undefined);

  const {
    isLoading: isLoadingCompanySpecificIndustry,
    companySpecificIndustryOptions,
    getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry,
  } = useGetCompanySpecificIndustryList();

  useEffect(() => {
    if (prevCompanySpecificIndustryIdRef.current === undefined) {
      prevCompanySpecificIndustryIdRef.current = companySpecificIndustryId;
      if (!companySpecificIndustryId) {
        setValue("specificBusinessClassificationIds", null, { shouldDirty: false });
      }
      return;
    }

    if (prevCompanySpecificIndustryIdRef.current !== companySpecificIndustryId) {
      prevCompanySpecificIndustryIdRef.current = companySpecificIndustryId;

      if (!companySpecificIndustryId) {
        setValue("specificBusinessClassificationIds", null, { shouldDirty: false });
        return;
      }

      const validOptions = getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry(companySpecificIndustryId);
      const validOptionValues = new Set(validOptions.map((opt) => opt.value));

      if (specificBusinessClassificationIds && Array.isArray(specificBusinessClassificationIds)) {
        const validValues = specificBusinessClassificationIds.filter((id) => id !== null && id !== undefined && validOptionValues.has(id));

        if (validValues.length !== specificBusinessClassificationIds.length) {
          setValue("specificBusinessClassificationIds", validValues.length > 0 ? validValues : null, { shouldDirty: false });
        }
      }
    }
  }, [
    companySpecificIndustryId,
    specificBusinessClassificationIds,
    getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry,
    setValue,
  ]);

  return (
    <Stack mt={2}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <FormField control={control} name="companySpecificIndustryId" required={!isDraft}>
            <Select placeholder="選択してください" options={companySpecificIndustryOptions} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="specificBusinessClassificationIds">
            <SelectMulti
              loading={isLoadingCompanySpecificIndustry}
              options={
                companySpecificIndustryId
                  ? getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry(companySpecificIndustryId)
                  : []
              }
              separator="、"
            />
          </FormField>
        </Grid>
      </Grid>
    </Stack>
  );
};
